import React, { useContext } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useTextDomainContext } from "@/app/providers";
import { bool, string, object, func, node, oneOfType } from "prop-types";

export interface ConfirmNotificationProps {
  isOpen?: boolean;
  open?: boolean;
  title?: string;
  message?: React.ReactNode;
  subMessage?: React.ReactNode;
  parameters?: any;
  result?: string;
  showButtonYes?: boolean;
  showButtonNo?: boolean;
  showButtonCancel?: boolean;
  showButtonConfirm?: boolean;
  confirmText?: string;
  confirmButtonText?: string;
  yesText?: string;
  cancelText?: string;
  cancelButtonText?: string;
  noText?: string;
  icon?: React.ReactNode;
  getResult?: (data: { result: "yes" | "no"; parameters: any }) => void;
  onClose?: () => void;
}

export const ConfirmNotification = (props: ConfirmNotificationProps) => {
  const { getResult, onClose } = props;
  const theme = useTheme();
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const propsDefault = {
    isOpen: false,
    title: gettext("Confirmation"),
    message: gettext("Are you sure you want to delete?"),
    parameters: {},
    result: "",
    showButtonYes: true,
    showButtonNo: true,
  };

  // Use props directly with fallbacks to avoid state management
  const isOpen = props.isOpen ?? props.open ?? propsDefault.isOpen;
  const title = props.title || propsDefault.title;
  const message = props.message || propsDefault.message;
  const subMessage = props.subMessage;
  const parameters = props.parameters || propsDefault.parameters;

  const shouldShowConfirm =
    props.showButtonConfirm ??
    props.showButtonYes ??
    propsDefault.showButtonYes;
  const shouldShowCancel =
    props.showButtonCancel ?? props.showButtonNo ?? propsDefault.showButtonNo;

  const cancelText =
    props.cancelButtonText ||
    props.cancelText ||
    props.noText ||
    gettext("Cancel");

  const confirmText =
    props.confirmButtonText ||
    props.confirmText ||
    props.yesText ||
    (props.title && props.title !== gettext("Confirmation")
      ? props.title
      : gettext("Delete"));

  const handleClickYes = () => {
    if (typeof getResult === "function") {
      getResult({
        result: "yes",
        parameters: parameters,
      });
    }
    if (typeof onClose === "function") {
      onClose();
    }
  };

  const handleClickNo = () => {
    if (typeof getResult === "function") {
      getResult({
        result: "no",
        parameters: parameters,
      });
    }
    if (typeof onClose === "function") {
      onClose();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClickNo}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            maxWidth: "480px",
            borderRadius: "10px",
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.08)",
            overflow: "hidden",
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper || "#FFFFFF",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor:
                (theme.palette.error as any)?.[50] ||
                alpha(theme.palette.error.main, 0.1) ||
                "#FFE6E6",
              color: theme.palette.error.main,
              flexShrink: 0,
            }}
          >
            {props.icon || <DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />}
          </Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              fontSize: "1rem",
              color: theme.palette.text.primary,
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClickNo}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            p: 0.5,
            "&:hover": {
              color: theme.palette.text.primary,
              backgroundColor: alpha(theme.palette.text.primary, 0.04),
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Content */}
      <DialogContent
        sx={{
          px: 3,
          py: 3.5,
          backgroundColor: theme.palette.background.paper || "#FFFFFF",
        }}
      >
        {typeof message === "string" ? (
          <Typography
            sx={{
              fontSize: "0.95rem",
              color: theme.palette.text.secondary,
              lineHeight: 1.5,
            }}
          >
            {message}
          </Typography>
        ) : (
          message
        )}
        {subMessage && (
          <Typography
            sx={{
              mt: 1,
              fontSize: "0.875rem",
              color: theme.palette.text.secondary,
            }}
          >
            {subMessage}
          </Typography>
        )}
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          backgroundColor: (theme.palette.grey as any)?.[50] || "#FAFAFA",
          borderTop: `1px solid ${theme.palette.divider}`,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
          m: 0,
        }}
      >
        {shouldShowCancel && (
          <Button
            variant="outlined"
            onClick={handleClickNo}
            sx={{
              backgroundColor: theme.palette.background.paper || "#FFFFFF",
              borderColor:
                theme.palette.divider ||
                (theme.palette.grey as any)?.[300] ||
                "#E0E0E0",
              color: theme.palette.text.primary,
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.875rem",
              px: 2.5,
              py: 0.75,
              borderRadius: "6px",
              boxShadow: "none",
              "&:hover": {
                backgroundColor:
                  (theme.palette.grey as any)?.[100] || "#F5F5F5",
                borderColor:
                  (theme.palette.grey as any)?.[400] || theme.palette.divider,
                boxShadow: "none",
              },
            }}
          >
            {cancelText}
          </Button>
        )}
        {shouldShowConfirm && (
          <Button
            variant="contained"
            onClick={handleClickYes}
            sx={{
              backgroundColor: theme.palette.error.main,
              color: "#FFFFFF",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              px: 2.5,
              py: 0.75,
              borderRadius: "6px",
              boxShadow: "none",
              "&:hover": {
                backgroundColor:
                  theme.palette.error.dark ||
                  (theme.palette.error as any)?.[900] ||
                  "#C62828",
                boxShadow: "none",
              },
            }}
          >
            {confirmText}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

ConfirmNotification.propTypes = {
  isOpen: bool,
  open: bool,
  title: string,
  message: oneOfType([string, node]),
  parameters: object,
  subMessage: oneOfType([string, node]),
  getResult: func,
  onClose: func,
  showButtonYes: bool,
  showButtonNo: bool,
  showButtonConfirm: bool,
  showButtonCancel: bool,
  confirmText: string,
  confirmButtonText: string,
  yesText: string,
  cancelText: string,
  cancelButtonText: string,
  noText: string,
  icon: node,
};
